/**
 * The marketing user-count figures, as pure functions: shared by the live-count
 * hook (`hooks/use-live-user-count`) and the edge middleware, which renders the
 * same figures into the HTML crawlers read.
 */

/**
 * Stands in while the request is in flight and whenever it fails. Deliberately
 * below the real total: the copy reads "more than", so a stale floor stays
 * true as the user base grows.
 */
export const FALLBACK_USER_COUNT = 2_000_000;

/**
 * Compact millions with one decimal — `1842652` → `1.8M`, `1250000` → `1.2M`.
 *
 * Rounded DOWN, never to nearest: the figure must not claim more users than
 * exist. The digits are locale-formatted (`1,8M` wherever the comma is the
 * decimal separator) while the `M` stays literal, so the line keeps the shape
 * every locale already ships. A runtime with a stubbed `Intl` falls back to
 * the plain English form rather than throwing inside render.
 */
export function formatCompactMillions(users: number, language = 'en'): string {
  const millions = Math.floor(Math.max(0, users) / 100_000) / 10;
  try {
    const digits = new Intl.NumberFormat(language, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(millions);
    return `${digits}M`;
  } catch {
    return `${millions.toFixed(1)}M`;
  }
}

/**
 * The whole number with the active locale's own grouping — `2023922` →
 * `2,023,922` in English and Hebrew, `2.023.922` in Italian, German and
 * Spanish, and the no-break space groups Intl hands French, Polish and
 * Swedish.
 *
 * Exact, never rounded: this is for copy that names a precise number of
 * students, so it may only be given a figure the route actually returned.
 * Callers with no live figure render {@link formatCompactMillions} with a
 * plus rather than a made-up total. A runtime with a stubbed `Intl` falls
 * back to ungrouped digits.
 */
export function formatExactCount(users: number, language = 'en'): string {
  const total = Math.floor(Math.max(0, users));
  try {
    return new Intl.NumberFormat(language).format(total);
  } catch {
    return String(total);
  }
}
